import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  useTheme,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import NotificationSnackbar from "./NotificationBar";
function EditGrobalPosition({ open, onClose, onSaveSuccess, position }) {
  const theme = useTheme();
  const [iscoData, setIscoData] = useState([]);
  const [selectedMajor, setSelectedMajor] = useState("");
  const [selectedSubMajor, setSelectedSubMajor] = useState("");
  const [selectedMinor, setSelectedMinor] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [loadingISCO, setLoadingISCO] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);
  useEffect(() => {
    if (open) {
      fetchISCO();
    }
  }, [open]);

  const fetchISCO = async () => {
    try {
      setLoadingISCO(true);
      const res = await fetch("/api/support/isco-occupation-details");
      const data = await res.json();
      setIscoData(data);
    } catch (err) {
      console.error("ISCO fetch error:", err);
    } finally {
      setLoadingISCO(false);
    }
  };
useEffect(() => {
  if (position && open) {
    setSelectedMajor(position.majorGroupId || "");
    setSelectedSubMajor(position.subMajorGroupId || "");
    setSelectedMinor(position.minorGroupId || "");
    setSelectedUnit(position.unitId || "");
  }
}, [position, open]);
  const subMajors =
    iscoData.find((m) => m.id === selectedMajor)?.subMajorGroups || [];
  const minors =
    subMajors.find((s) => s.id === selectedSubMajor)?.minorGroups || [];
  const units = minors.find((m) => m.id === selectedMinor)?.units || [];
const handleSave = async () => {
  if (!position?.id) {
    setSnackbarMessage("Invalid position");
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
    return;
  }
    setSaving(true);
    try {
      const res = await fetch(`/api/support/update-isco-unit/${position.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          iscoUnitId: selectedUnit,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Update failed");
      }
      setSnackbarMessage("ISCO Unit updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setSaved(true);

      setTimeout(() => {
        setSaved(false);
        onSaveSuccess?.();
        onClose();
      }, 1200);
    } catch (err) {
      console.error(err);
      setSnackbarMessage(err.message || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };
  const handleClose = () => {
    setSelectedMajor("");
    setSelectedSubMajor("");
    setSelectedMinor("");
    setSelectedUnit("");
    setSaved(false);
    onClose();
  };
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          width: "550px",
          maxWidth: "90%",
        },
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: theme.palette.primary.light, px: 2.5, py: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon sx={{ color: "#fff" }} />
            <Typography sx={{ color: "#fff", fontWeight: 500 }}>
              Edit ISCO Grobal Positions Mapping
            </Typography>
          </Box>

          <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Body */}
      <DialogContent sx={{ pt: 2 }}>
        {loadingISCO ? (
          <Box textAlign="center">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Major */}
            <TextField
              select
              label="Major Group"
              size="small"
              value={selectedMajor}
              onChange={(e) => {
                setSelectedMajor(e.target.value);
                setSelectedSubMajor("");
                setSelectedMinor("");
                setSelectedUnit("");
              }}
              sx={fieldSx}
              fullWidth
            >
              {iscoData.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Sub Major */}
            <TextField
              select
              label="Sub Major"
              size="small"
              value={selectedSubMajor}
              onChange={(e) => {
                setSelectedSubMajor(e.target.value);
                setSelectedMinor("");
                setSelectedUnit("");
              }}
              disabled={!selectedMajor}
              sx={fieldSx}
              fullWidth
            >
              {subMajors.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Minor */}
            <TextField
              select
              label="Minor Group"
              size="small"
              value={selectedMinor}
              onChange={(e) => {
                setSelectedMinor(e.target.value);
                setSelectedUnit("");
              }}
              disabled={!selectedSubMajor}
              sx={fieldSx}
              fullWidth
            >
              {minors.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Unit */}
            <TextField
              select
              label="ISCO Unit"
              size="small"
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              disabled={!selectedMinor}
              sx={fieldSx}
              fullWidth
            >
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Tooltip title={!selectedUnit ? "Please select ISCO Unit" : ""}>
          <span>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={saving || saved || !selectedUnit}
            >
              Save
            </Button>
          </span>
        </Tooltip>
      </DialogActions>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default EditGrobalPosition;
